import React from 'react';
import { useSelector } from 'react-redux';
import T from 'prop-types';
import { isEmpty } from 'lodash-es';

import skeletons from './Skeletons';
import Tooltip from '../../../../Tooltip';
import ErrorStateWithReload from '../../../../ErrorStateWithReload';
import Gauge from '../../../../../pages/app-insights/_shared/components/Gauge';
import InsightsCardBox from '../../../../../pages/app-insights/_shared/components/InsightsCardBox';
import PerformanceSnapshotTable from '../../../../../pages/app-insights/_shared/components/PerformanceSnapshotTable';
import ReleasedWithinLastYearNotification from '../../../../../pages/app-insights/app-overview/_shared/components/ReleasedWithinLastYearNotification';
import { permissionsSelector } from '../../../selectors';
import { sortSegmentsData } from '../../../../../pages/app-insights/_shared/config/finance/cohorts';

const widgetsTypes = {
  growth: 'Growth',
  engagement: 'Engagement',
};

const widgetsTooltipForFinanceInsights = {
  growth: 'Growth is calculated using the percent change of Downloads year-over-year for the preceding 12 weeks.',
  engagement: 'Engagement is calculated used the percent change of Monthly Active Users (MAU) year-over-year for the preceding 12 weeks.',
};

const emptyArray = [];
const emptyObject = {};

const FinanceGaugeInsights = ({ widgets, settings, releasedNotification }) => {
  const { finance_insights = emptyObject } = widgets;
  const { pending, error } = settings;

  if (error) return <ErrorStateWithReload />;

  return (
    <div className="finance-gauge-widgets">
      {Object.keys(widgetsTypes).map(widget => (
        <InsightsCardBox
          key={widgetsTypes[widget]}
          bodyMinHeight={155}
          className="app-insights-widget card-box-sm"
          headerLeft={(
            <div className="h4 d-flex align-items-center">
              {widgetsTypes[widget]}
              <Tooltip content={widgetsTooltipForFinanceInsights[widget]}>
                <span className="cursor-pointer tooltip-icon">
                  <i className="hicon-question-circle" />
                </span>
              </Tooltip>
            </div>
          )}
        >
          {pending
            ? skeletons.widgetInsightsGauge
            : (releasedNotification || <Gauge value={finance_insights[widget] || 'Up'} />)}
        </InsightsCardBox>
      ))}
    </div>
  );
};

FinanceGaugeInsights.propTypes = {
  widgets: T.object.isRequired,
  settings: T.object.isRequired,
  releasedNotification: T.node,
};

const decorateAdvancedInsights = (insights) => {
  if (isEmpty(insights) || insights.no_data) return emptyArray;

  const { groupedSegments } = sortSegmentsData(insights.all);

  return groupedSegments.all;
};

const FinancePerformanceSnapshot = ({
  widgets,
  settings,
  isAdvancedDataAbsent,
  releasedNotification,
}) => {
  const { metrics_insights = emptyArray, advanced_metrics_insights = emptyObject } = widgets;
  const { pending, error } = settings;

  const getData = () => {
    if (isAdvancedDataAbsent) return metrics_insights[0] || {};

    return decorateAdvancedInsights(advanced_metrics_insights)[0] || {};
  };

  if (error) return <ErrorStateWithReload />;

  return (
    <InsightsCardBox
      bodyMinHeight={118}
      className="finance-performance-snapshot card-box-sm"
      headerLeft={<h4 className="h4">Performance Snapshot</h4>}
    >
      {pending ? skeletons.performanceSnapshot : (
        <PerformanceSnapshotTable
          data={getData()}
          releasedNotification={releasedNotification}
        />
      )}
    </InsightsCardBox>
  );
};

FinancePerformanceSnapshot.propTypes = {
  widgets: T.object.isRequired,
  isAdvancedDataAbsent: T.bool,
  settings: T.object.isRequired,
  releasedNotification: T.node,
};

const FinanceInsightsWidgets = ({
  app,
  widgets,
  isAdvancedDataAbsent,
  settings,
}) => {
  const permissions = useSelector(permissionsSelector);

  const releasedNotification = widgets.released_within_last_year && (
    <ReleasedWithinLastYearNotification
      link={`/apps/${app.store}/${app.ref_no}/performance`}
    />
  );

  return permissions.allowFinanceAppOverviewV3 ? (
    <FinancePerformanceSnapshot
      widgets={widgets}
      settings={settings}
      isAdvancedDataAbsent={isAdvancedDataAbsent}
      releasedNotification={releasedNotification}
    />
  ) : (
    <FinanceGaugeInsights
      widgets={widgets}
      settings={settings}
      releasedNotification={releasedNotification}
    />
  );
};

FinanceInsightsWidgets.propTypes = {
  app: T.object.isRequired,
  widgets: T.object.isRequired,
  isAdvancedDataAbsent: T.bool,
  settings: T.object.isRequired,
};

export default FinanceInsightsWidgets;
